import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import { claudeTokens } from '@/styles/claudeTokens';

export interface CarouselBanner {
    id: number;
    title?: string;
    imageUrl: string;
    mobileImageUrl?: string | null;
    linkUrl?: string | null;
}

/** 넘김 틀은 부모 높이를 채운다 — 비율·모서리·테두리는 부모(홈 메인 배너 HeroWrap, 페이지 배너 틀)가 정한다 */
const Root = styled.div`
    width: 100%;
    height: 100%;

    .swiper, .swiper-wrapper, .swiper-slide {
        height: 100%;
    }
`;

const Slide = styled.div`
    width: 100%;
    height: 100%;
    cursor: pointer;
    overflow: hidden;
    background: ${claudeTokens.color.bg};

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

/**
 * 배너 넘김 — 홈 메인 배너(HeroBanner)와 페이지 상단·하단 배너(PageBannerSlot)가 같이 쓴다.
 * 인덱스 표시(점/카운터) 없이 4초 자동 넘김 + 손으로 넘기기, 2장 이상이면 끝에서 처음으로 이어진다(2026-09-18 홈 기준).
 * 링크: 명치사 주소·상대 경로는 화면 안 이동, 다른 사이트는 같은 창 이동.
 */
export default function BannerCarousel({ banners, className, noSwipeBack = false }: { banners: CarouselBanner[]; className?: string; noSwipeBack?: boolean }) {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const handleClick = (b: CarouselBanner) => {
        if (!b.linkUrl) return;
        // 절대 URL이면 명치사 도메인은 SPA 경로로 변환, 아니면 같은 창에서 외부 이동
        if (b.linkUrl.startsWith('http://') || b.linkUrl.startsWith('https://')) {
            try {
                const url = new URL(b.linkUrl);
                if (url.hostname.includes('myungchisa.com') || url.origin === window.location.origin) {
                    navigate(url.pathname + url.search + url.hash);
                    return;
                }
            } catch {}
            window.location.href = b.linkUrl;
            return;
        }
        navigate(b.linkUrl);
    };

    return (
        <Root className={className} {...(noSwipeBack ? { 'data-no-swipe-back': '' } : {})}>
            <Swiper
                modules={[Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                loop={banners.length > 1}
            >
                {banners.map((b) => (
                    <SwiperSlide key={b.id}>
                        <Slide onClick={() => handleClick(b)} data-banner-id={b.id}>
                            <img
                                src={isMobile && b.mobileImageUrl ? b.mobileImageUrl : b.imageUrl}
                                alt={b.title || 'banner'}
                            />
                        </Slide>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Root>
    );
}
